import { useState, useRef } from 'react';
import { supabase } from '../utils/supabase';
import { getEmailConfirmationRedirectUrl } from '../utils/redirectUrls';
import { CAPTCHA_CONFIG } from '../config/captcha';
import { validateRegistrationForm, validateLoginForm } from '../utils/validation';
import { resetPassword } from '../services/authService';
import { PENDING_REGISTRATION_STORAGE_KEY } from '../services/playerOnboardingService';
import { signInWithGoogle } from '../services/oauthService';

const EMPTY_FORM = {
  email: '',
  password: '',
  first_name: '',
  last_name: '',
  gender: '',
};

/**
 * Shared login/register form state. Auth methods, validation, Captcha,
 * verification, and password-reset behaviour match the previous App.js gate.
 */
export default function useAuthForm({
  mode,
  sessionRef,
  setAuthResolving,
  initialSuccess = null,
} = {}) {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(initialSuccess);
  const [submitting, setSubmitting] = useState(false);

  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordError, setForgotPasswordError] = useState(null);
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(null);
  const [isResettingPassword, setIsResettingPassword] = useState(false);

  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaError, setCaptchaError] = useState(null);
  const [showCaptchaField, setShowCaptchaField] = useState(false);
  const captchaRef = useRef(null);

  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [validationErrors, setValidationErrors] = useState({});

  const revealCaptchaField = () => {
    if (!CAPTCHA_CONFIG.SKIP_UI) {
      setShowCaptchaField(true);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    if ((name === 'email' || name === 'password') && value.trim()) {
      revealCaptchaField();
    }

    if (validationErrors[name]) {
      setValidationErrors({
        ...validationErrors,
        [name]: undefined,
      });
    }
  };

  const handleCaptchaVerify = (token) => {
    setCaptchaToken(token);
    setCaptchaError(null);
  };

  const handleCaptchaExpire = () => {
    setCaptchaToken(null);
    setCaptchaError('CAPTCHA expired. Please verify again.');
  };

  const handleCaptchaError = () => {
    setCaptchaToken(null);
    setCaptchaError('CAPTCHA verification failed. Please try again.');
  };

  const handleForgotPassword = () => {
    setForgotPasswordEmail(formData.email || '');
    setForgotPasswordError(null);
    setForgotPasswordSuccess(null);
    setShowForgotPasswordModal(true);
  };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    setForgotPasswordError(null);
    setForgotPasswordSuccess(null);

    try {
      if (!forgotPasswordEmail) {
        setForgotPasswordError('Please enter your email address.');
        return;
      }

      setIsResettingPassword(true);
      await resetPassword(forgotPasswordEmail.trim().toLowerCase());
      setForgotPasswordSuccess('If there is an account with that email, a password reset link has been sent.');
    } catch (err) {
      console.error('Forgot password error:', err);
      setForgotPasswordError(err.message || 'Failed to send password reset email. Please try again.');
    } finally {
      setIsResettingPassword(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setCaptchaError(null);
    setValidationErrors({});

    const validation = validateLoginForm(formData);
    if (!validation.isValid) {
      setValidationErrors(validation.errors);
      return;
    }

    if (!CAPTCHA_CONFIG.SKIP_UI && !CAPTCHA_CONFIG.IS_CONFIGURED) {
      setCaptchaError('Sign-in is temporarily unavailable. Please try again later.');
      return;
    }
    if (!CAPTCHA_CONFIG.SKIP_UI && !captchaToken) {
      revealCaptchaField();
      setCaptchaError('Please complete the CAPTCHA verification.');
      return;
    }

    setSubmitting(true);

    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email: validation.data.email,
        password: validation.data.password,
      });

      if (signInError) throw signInError;
      if (data?.session) {
        if (sessionRef) sessionRef.current = data.session;
        if (typeof setAuthResolving === 'function') setAuthResolving(true);
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'Login failed. Please try again.');
      if (captchaRef.current) {
        captchaRef.current.reset();
      }
      setCaptchaToken(null);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    console.log('Registration form submitted!');
    console.log('Form data:', formData);

    setError(null);
    setSuccess(null);
    setCaptchaError(null);
    setValidationErrors({});

    const validation = validateRegistrationForm(formData);
    console.log('Validation result:', validation);
    if (!validation.isValid) {
      console.log('Validation failed:', validation.errors);
      setValidationErrors(validation.errors);
      return;
    }

    if (!CAPTCHA_CONFIG.SKIP_UI && !CAPTCHA_CONFIG.IS_CONFIGURED) {
      setCaptchaError('Registration is temporarily unavailable. Please try again later.');
      return;
    }
    if (!CAPTCHA_CONFIG.SKIP_UI && !captchaToken) {
      revealCaptchaField();
      setCaptchaError('Please complete the CAPTCHA verification.');
      return;
    }

    setSubmitting(true);

    try {
      console.log('Starting registration...');

      localStorage.setItem(PENDING_REGISTRATION_STORAGE_KEY, JSON.stringify({
        first_name: validation.data.first_name,
        last_name: validation.data.last_name,
        gender: validation.data.gender,
      }));

      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: validation.data.email,
        password: validation.data.password,
        options: {
          data: {
            first_name: validation.data.first_name,
            last_name: validation.data.last_name,
            gender: validation.data.gender,
          },
          emailRedirectTo: getEmailConfirmationRedirectUrl(),
        },
      });

      console.log('Auth signup result:', authData, authError);

      if (authError) throw authError;

      if (authData.user) {
        console.log('Auth account created successfully');

        setVerificationEmail(validation.data.email);
        setShowVerificationModal(true);

        setFormData({ ...EMPTY_FORM });
        setError(null);
        setSuccess(null);
        setValidationErrors({});
      } else {
        throw new Error('No user data returned from signup');
      }
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.message || 'Registration failed. Please try again.');
      if (captchaRef.current) {
        captchaRef.current.reset();
      }
      setCaptchaToken(null);
    } finally {
      console.log('Registration process complete');
      setSubmitting(false);
    }
  };

  const handleGoogleSignIn = async () => {
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      await signInWithGoogle();
    } catch (err) {
      console.error('[AUTH] Google sign-in error:', err);
      setError(err.message || 'Google sign-in failed. Please try again.');
      setSubmitting(false);
    }
  };

  const handleSubmit = (e) => {
    console.log('Form submitted, authMode:', mode);
    if (mode === 'login') {
      handleLogin(e);
    } else {
      handleRegister(e);
    }
  };

  return {
    formData,
    error,
    success,
    setSuccess,
    submitting,
    validationErrors,
    captchaRef,
    captchaError,
    showCaptchaField,
    revealCaptchaField,
    handleInputChange,
    handleCaptchaVerify,
    handleCaptchaExpire,
    handleCaptchaError,
    handleSubmit,
    handleForgotPassword,
    handleForgotPasswordSubmit,
    showForgotPasswordModal,
    setShowForgotPasswordModal,
    forgotPasswordEmail,
    setForgotPasswordEmail,
    forgotPasswordError,
    forgotPasswordSuccess,
    isResettingPassword,
    handleGoogleSignIn,
    showVerificationModal,
    setShowVerificationModal,
    verificationEmail,
  };
}
